<script setup lang="ts">
import { computed, ref } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import AuthSupportLinks from '@/components/views/manage-account/AuthSupportLinks.vue'
import unknownEmail from '@/assets/images/unknown-email.svg'
import warningTriangle from '@/assets/images/warning-triangle.svg'

const store = useStore()
const router = useRouter()

const email = computed(() => store.state.auth.authEmail)
const scheduledForDeletion = computed(
  () => store.state.donor.scheduledForDeletion
)

const cancelLoading = ref(false)
const cancelFailed = ref(false)

const cancelDeletion = async () => {
  if (cancelLoading.value) return
  cancelLoading.value = true
  cancelFailed.value = false
  try {
    await store.dispatch('cancelDonorDeletion')
    // Account restored — let the dashboard guards route to the right landing page.
    await router.push({ name: 'Dashboard' })
  } catch {
    cancelFailed.value = true
  } finally {
    cancelLoading.value = false
  }
}

const logout = () => {
  store.dispatch('auth0Logout')
}
</script>

<template>
  <div class="sa-auth-step sa-auth-user-not-found">
    <!-- Donor scheduled for deletion (POST /me 409) -->
    <template v-if="scheduledForDeletion">
      <header class="sa-auth-step__header">
        <img
          :src="warningTriangle"
          class="sa-auth-status-page__swifty"
          alt=""
          aria-hidden="true"
          width="84"
          height="84"
        />
        <p class="sa-auth-step__eyebrow">
          {{ $t('ManageAccount.UserNotFound.ScheduledForDeletion.Eyebrow') }}
        </p>
        <h1 class="sa-auth-step__title">
          {{ $t('ManageAccount.UserNotFound.ScheduledForDeletion.Header') }}
        </h1>
      </header>

      <p>
        {{ $t('ManageAccount.UserNotFound.ScheduledForDeletion.Paragraph') }}
        <span v-if="email" class="sa-auth-user-not-found__email">
          {{ email }}
        </span>
      </p>

      <div class="sa-auth-user-not-found__actions">
        <button
          type="button"
          class="sa-btn-primary"
          data-testid="cancel-deletion-button"
          :disabled="cancelLoading"
          :aria-busy="cancelLoading"
          @click="cancelDeletion"
        >
          <span
            v-if="cancelLoading"
            class="spinner sa-btn-spinner"
            aria-hidden="true"
          />
          <span>
            {{
              $t(
                'ManageAccount.UserNotFound.ScheduledForDeletion.CancelDeletion'
              )
            }}
          </span>
        </button>
        <button
          type="button"
          class="sa-btn-secondary"
          data-testid="logout-button"
          @click="logout"
        >
          {{ $t('ManageAccount.UserNotFound.ScheduledForDeletion.Logout') }}
        </button>
      </div>

      <p v-if="cancelFailed" class="sa-field-error sa-field-error--center">
        {{ $t('AxiosError.Default') }}
      </p>

      <AuthSupportLinks lead-key="ManageAccount.UserNotFound.SupportLead" />
    </template>

    <!-- No donor account found (GET /me 404) -->
    <template v-else>
      <header class="sa-auth-step__header">
        <img
          :src="unknownEmail"
          class="sa-auth-step__illustration"
          alt=""
          aria-hidden="true"
          width="96"
          height="76"
        />
        <p class="sa-auth-step__eyebrow">
          {{ $t('ManageAccount.UserNotFound.Eyebrow') }}
        </p>
        <h1 class="sa-auth-step__title">
          {{ $t('ManageAccount.UserNotFound.Header') }}
        </h1>
      </header>

      <p v-if="email">
        {{ $t('ManageAccount.UserNotFound.Paragraph') }}
        <span class="sa-auth-user-not-found__email">{{ email }}</span>
      </p>
      <p v-else>{{ $t('ManageAccount.UserNotFound.ParagraphWithEmail') }}</p>
      <p>{{ $t('ManageAccount.UserNotFound.Instruction') }}</p>

      <div class="sa-auth-user-not-found__actions">
        <router-link :to="{ name: 'Login' }" class="sa-btn-primary">
          {{ $t('ManageAccount.UserNotFound.Button') }}
        </router-link>
        <router-link :to="{ name: 'Signup' }" class="sa-btn-secondary">
          {{ $t('ManageAccount.UserNotFound.SignupCta') }}
        </router-link>
      </div>

      <AuthSupportLinks lead-key="ManageAccount.UserNotFound.SupportLead" />
    </template>
  </div>
</template>
